import React, { useContext, useEffect, useRef, useState } from 'react';
import { PageContext } from '../frontend/pageContext';
import TocItem from './TocItem';

const HEADER_OFFSET = 175;
const SCROLL_END_THRESHOLD = 5;
const CLICK_DEBOUNCE_MS = 800;
const HEADING_CLICK_DEBOUNCE_MS = 400;

/**
 * Scrolls to element by id, updates URL and active TOC state, then resets click flag.
 */
function scrollToSection(
  id: string,
  setActiveId: (id: string) => void,
  isClickItem: React.MutableRefObject<boolean>,
  debounceMs: number,
): void {
  const el = document.getElementById(id);
  if (!el) return;

  isClickItem.current = true;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  history.replaceState(null, '', `#${id}`);
  setActiveId(id);
  setTimeout(() => {
    isClickItem.current = false;
  }, debounceMs);
}

const TableOfContents = () => {
  const { toc } = useContext(PageContext);
  const [activeId, setActiveId] = useState<string | null>(null);
  const isClickItem = useRef(false);

  const handleClick = (id: string): void => {
    scrollToSection(id, setActiveId, isClickItem, CLICK_DEBOUNCE_MS);
  };

  useEffect(() => {
    if (!toc?.length) return;

    setActiveId((prev) => prev ?? toc[0].id);

    const sections = Array.from(document.querySelectorAll('h1, h2'));
    const handleScroll = (): void => {
      if (isClickItem.current) return;

      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - SCROLL_END_THRESHOLD;
      if (atBottom) {
        setActiveId(toc[toc.length - 1].id);
        return;
      }

      const firstSpan = sections[0]?.querySelector<HTMLSpanElement>('span[id]');
      let currentId = firstSpan?.id ?? null;

      for (const section of sections) {
        const spanWithId = section.querySelector<HTMLSpanElement>('span[id]');
        if (!spanWithId) continue;

        const top = section.getBoundingClientRect().top;
        if (top <= HEADER_OFFSET) {
          currentId = spanWithId.id;
        } else {
          break;
        }
      }
      if (currentId !== null) {
        setActiveId(currentId);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [toc]);

  useEffect(() => {
    const handleHeadingClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      const heading = target.closest('h1, h2');
      if (!heading) return;
      const spanWithId = heading.querySelector<HTMLSpanElement>('span[id]');
      if (!spanWithId) return;

      scrollToSection(
        spanWithId.id,
        setActiveId,
        isClickItem,
        HEADING_CLICK_DEBOUNCE_MS,
      );
    };

    document.addEventListener('click', handleHeadingClick);
    return () => document.removeEventListener('click', handleHeadingClick);
  }, []);

  return (
    <>
      {toc && toc.length > 0 && (
        <div className="table-content" data-test-id="toc">
          <h3>Table of Contents</h3>
          <ul className="toc">
            {toc.map((item) => (
              <TocItem
                item={item}
                key={item.id}
                onClick={(e) => {
                  e.preventDefault();
                  handleClick(item.id);
                }}
                isActive={activeId === item.id}
              />
            ))}
          </ul>
          <hr style={{ marginBottom: '16px' }} />
        </div>
      )}
    </>
  );
};

export default TableOfContents;
